import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, UploadCloud } from "lucide-react";
import { getAnnouncements, addAnnouncement, editAnnouncement, removeAnnouncement, uploadAnnouncementImage } from "@/api/announcements";
import { HtmlEditor } from "@/components/admin/HtmlEditor";
import { api } from "@/lib/supabase";

const empty = { titleTr: "", titleEn: "", contentTr: "", contentEn: "", imageUrl: "", published: true };

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const edit = searchParams.get("edit") || undefined;
  const lang = (searchParams.get("lang") as "tr" | "en") || "tr";
  const activeLang = lang;

  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [translating, setTranslating] = useState(false);

  const translateText = async (text: string, from: string, to: string): Promise<string> => {
    if (!text || !text.trim()) return "";
    try {
      const data = await api.post<any>('/settings/translate.php', { text, from, to });
      return data[0].map((x: any) => x[0]).join("");
    } catch (err) {
      console.error("Translation error:", err);
      throw err;
    }
  };

  const handleAutoTranslate = async (from: "tr" | "en", to: "tr" | "en") => {
    if (!editing) return;
    
    const destTitle = to === "tr" ? editing.titleTr : editing.titleEn;
    if (destTitle && destTitle.trim() && !confirm("Hedef dildeki mevcut içeriklerin üzerine yazılacak. Devam etmek istiyor musunuz?")) {
      return;
    }

    setTranslating(true);
    const toastId = toast.loading("Çeviriliyor, lütfen bekleyin...");
    try {
      const srcTitle = from === "tr" ? editing.titleTr : editing.titleEn;
      const srcContent = from === "tr" ? editing.contentTr : editing.contentEn;

      const translatedTitle = await translateText(srcTitle, from, to);
      const translatedContent = await translateText(srcContent, from, to);

      setEditing((prev: any) => {
        if (!prev) return null;
        if (to === "en") {
          return {
            ...prev,
            titleEn: translatedTitle,
            contentEn: translatedContent
          };
        } else {
          return {
            ...prev,
            titleTr: translatedTitle,
            contentTr: translatedContent
          };
        }
      });
      
      toast.success("Çeviri başarıyla tamamlandı!", { id: toastId });
    } catch (err: any) {
      toast.error("Çeviri sırasında bir hata oluştu: " + err.message, { id: toastId });
    } finally {
      setTranslating(false);
    }
  };

  const load = async () => {
    try {
      const data = await getAnnouncements(false);
      setList(data ?? []);
    } catch (err) {
      console.error('Failed to load announcements:', err);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!edit) {
      setEditing(null);
    } else if (edit === "new") {
      setEditing({ ...empty });
    } else if (list.length > 0) {
      const found = list.find(a => a.id === edit);
      if (found) {
        // Map API response to component state (snake_case to camelCase)
        setEditing({
          id: found.id,
          titleTr: found.title_tr,
          titleEn: found.title_en,
          contentTr: found.content_tr,
          contentEn: found.content_en,
          imageUrl: found.image_url,
          published: found.published,
        });
      }
    }
  }, [edit, list]);

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result as string;
        const url = await uploadAnnouncementImage(file);
        setEditing((prev: any) => prev ? { ...prev, imageUrl: url } : null);
        toast.success('Görsel başarıyla yüklendi!');
      } catch (err: any) {
        toast.error('Yükleme başarısız: ' + err.message);
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      toast.error('Dosya okunamadı.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    const p = { ...editing };
    const isNew = !p.id;
    try {
      const apiPayload: any = {
        title_tr: p.titleTr,
        title_en: p.titleEn,
        content_tr: p.contentTr,
        content_en: p.contentEn,
        image_url: p.imageUrl,
        published: p.published,
      };
      if (isNew) {
        await addAnnouncement(apiPayload);
      } else {
        await editAnnouncement({ id: p.id, updates: apiPayload });
      }
      toast.success("Kaydedildi");
      setSearchParams({});
      load();
    } catch (err: any) {
      toast.error("Hata: " + (err.message || err));
    }
  };

  const del = async (id: string) => {
    if (!confirm("Silinsin mi?")) return;
    try {
      await removeAnnouncement(id);
      toast.success("Silindi");
      load();
    } catch (err: any) {
      toast.error("Hata: " + (err.message || err));
    }
  };

  return (
    <div>
      {editing ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center border-b pb-4">
            <div className="space-y-1">
              <button
                onClick={() => setSearchParams({})}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
              >
                <ArrowLeft size={16} />
                ← Duyuru Listesine Dön
              </button>
              <h1 className="text-2xl font-bold text-primary">
                {editing.id ? "Duyuruyu Düzenle" : "Yeni Duyuru Ekle"}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSearchParams({})}
                className="px-4 py-2 text-sm font-semibold rounded-lg border bg-background hover:bg-muted cursor-pointer transition-colors"
              >
                İptal
              </button>
              <button
                onClick={save}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 cursor-pointer transition-colors shadow-sm"
              >
                Kaydet
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sol Kolon - Geniş İçerik Alanı (2/3 Genişlik) */}
            <div className="lg:col-span-2 flex">
              <div className="bg-card border rounded-xl p-6 shadow-2xs flex-1 flex flex-col space-y-4">
                <div className="flex justify-between items-center border-b pb-2 mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider text-muted-foreground">Dil Ayarlı İçerik Tanımları</h3>
                    <button
                      type="button"
                      onClick={() => handleAutoTranslate(activeLang, activeLang === "tr" ? "en" : "tr")}
                      disabled={translating}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      {translating ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          Çeviriliyor...
                        </>
                      ) : activeLang === "tr" ? (
                        "Otomatik Çevir (TR ➔ EN)"
                      ) : (
                        "Otomatik Çevir (EN ➔ TR)"
                      )}
                    </button>
                  </div>
                  <div className="flex bg-muted p-1 rounded-lg border text-xs gap-1">
                    <button
                      onClick={() => setSearchParams({ edit: edit || "", lang: "tr" })}
                      className={`px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                        activeLang === "tr"
                          ? "bg-background text-foreground shadow-2xs border"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Türkçe (TR)
                    </button>
                    <button
                      onClick={() => setSearchParams({ edit: edit || "", lang: "en" })}
                      className={`px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                        activeLang === "en"
                          ? "bg-background text-foreground shadow-2xs border"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      İngilizce (EN)
                    </button>
                  </div>
                </div>

                {activeLang === "tr" ? (
                  <div className="space-y-4 flex-1 flex flex-col">
                    <Row label="Duyuru Başlığı (Türkçe)">
                      <input
                        className="inp"
                        value={editing.titleTr}
                        onChange={e => setEditing({ ...editing, titleTr: e.target.value })}
                      />
                    </Row>

                    <Row label="Duyuru İçeriği (Türkçe)" className="flex-1">
                      <HtmlEditor
                        value={editing.contentTr ?? ""}
                        onChange={val => setEditing({ ...editing, contentTr: val })}
                        className="flex-1"
                      />
                    </Row>
                  </div>
                ) : (
                  <div className="space-y-4 flex-1 flex flex-col">
                    <Row label="Duyuru Başlığı (İngilizce)">
                      <input
                        className="inp"
                        value={editing.titleEn}
                        onChange={e => setEditing({ ...editing, titleEn: e.target.value })}
                      />
                    </Row>

                    <Row label="Duyuru İçeriği (İngilizce)" className="flex-1">
                      <HtmlEditor
                        value={editing.contentEn ?? ""}
                        onChange={val => setEditing({ ...editing, contentEn: val })}
                        className="flex-1"
                      />
                    </Row>
                  </div>
                )}
              </div>
            </div>

            {/* Sağ Kolon - Yan Ayarlar Paneli (1/3 Genişlik) */}
            <div className="space-y-6">
              <div className="bg-card border rounded-xl p-6 shadow-2xs space-y-5">
                <h3 className="font-bold text-slate-800 border-b pb-2 text-sm uppercase tracking-wider text-muted-foreground">Sistem & Görünüm Ayarları</h3>

                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-700">Duyuru Görseli</label>
                  
                  <div
                    onDragOver={e => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleFileDrop}
                    onClick={() => document.getElementById("file-upload-input")?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all flex flex-col items-center justify-center min-h-[120px] ${
                      dragging
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-muted-foreground/20 hover:border-primary/50 bg-background text-muted-foreground"
                    }`}
                  >
                    <input
                      id="file-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs font-semibold text-primary">Görsel yükleniyor...</span>
                      </div>
                    ) : editing.imageUrl ? (
                      <div className="space-y-2 w-full flex flex-col items-center">
                        <div className="relative group w-full rounded-lg overflow-hidden border bg-muted aspect-video">
                          <img
                            src={editing.imageUrl}
                            alt="Duyuru Görseli"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <span className="text-[11px] text-white font-bold">Görseli Değiştir</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground break-all line-clamp-1">
                          {editing.imageUrl}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 p-2">
                        <div className="w-8 h-8 rounded-full bg-accent/50 flex items-center justify-center text-muted-foreground mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4.5 h-4.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                          </svg>
                        </div>
                        <span className="text-xs font-semibold text-foreground">Görseli sürükle bırak veya seç</span>
                        <span className="text-[10px] text-muted-foreground">PNG, JPG, WebP veya SVG (En fazla 10MB)</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-2.5">
                    <input
                      className="inp text-xs font-mono"
                      value={editing.imageUrl ?? ""}
                      onChange={e => setEditing({ ...editing, imageUrl: e.target.value })}
                      placeholder="Görsel URL / Dosya yolu (Örn: /images/duyuru.jpg)"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                      checked={editing.published}
                      onChange={e => setEditing({ ...editing, published: e.target.checked })}
                    />
                    <span className="text-sm font-semibold text-foreground">Duyuru Yayında</span>
                  </label>
                  <p className="text-[11px] text-muted-foreground ml-6 mt-0.5">Ziyaretçiler bu duyuruyu görebilir</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-primary">Duyurular</h1>
            <button
              onClick={() => setSearchParams({ edit: "new", lang: "tr" })}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity font-semibold text-sm shadow-sm"
            >
              <Plus size={16}/> Yeni Duyuru Ekle
            </button>
          </div>
          <div className="bg-card border rounded-xl overflow-hidden shadow-2xs">
            <table className="w-full text-sm">
              <thead className="bg-accent/30 text-muted-foreground font-semibold">
                <tr className="border-b">
                  <th className="text-left p-3.5">Tarih</th>
                  <th className="text-left p-3.5">Duyuru Başlığı</th>
                  <th className="text-left p-3.5">Durum</th>
                  <th className="text-right p-3.5">İşlem (Dil)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.map(a => (
                  <tr key={a.id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3.5 text-muted-foreground">{new Date(a.created_at).toLocaleDateString("tr-TR")}</td>
                    <td className="p-3.5 font-medium">{a.title_tr || <span className="text-muted-foreground italic">Başlıksız</span>}</td>
                    <td className="p-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.published ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-700 border border-slate-200"
                      }`}>
                        {a.published ? "Yayında" : "Taslak"}
                      </span>
                    </td>
                    <td className="p-3.5 text-right">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => setSearchParams({ edit: a.id, lang: "tr" })}
                          className={`px-2.5 py-1 text-xs font-semibold rounded cursor-pointer transition-colors ${
                            a.title_tr && a.content_tr
                              ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              : "bg-rose-50 text-rose-600 hover:bg-rose-100"
                          }`}
                        >
                          TR
                        </button>
                        <button
                          onClick={() => setSearchParams({ edit: a.id, lang: "en" })}
                          className={`px-2.5 py-1 text-xs font-semibold rounded cursor-pointer transition-colors ${
                            a.title_en && a.content_en
                              ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              : "bg-rose-50 text-rose-600 hover:bg-rose-100"
                          }`}
                        >
                          EN
                        </button>
                        <button
                          onClick={() => del(a.id)}
                          className="p-1.5 hover:bg-destructive/10 text-destructive rounded cursor-pointer transition-colors ml-1"
                        >
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center p-8 text-muted-foreground">Henüz duyuru eklenmemiş.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <style>{`
        .inp {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 12px;
          background: var(--background);
          font-size: 0.875rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .inp:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.15);
        }
      `}</style>
    </div>
  );
}

function Row({ label, children, className = "", ...props }: any) {
  return (
    <div className={`flex flex-col ${className}`} {...props}>
      <label className="block text-sm font-medium mb-1 text-slate-700">{label}</label>
      {children}
    </div>
  );
}
